import { useState } from 'react';
import { match } from 'ts-pattern';
import { MediaFallback } from './MediaFallback';
import { MediaFull } from './MediaFull';
import { MediaLightbox } from './MediaLightbox';
import { MediaThumbnail } from './MediaThumbnail';
import { useMediaContentType } from './useMediaContentType';

export function MediaPreview({
  src,
  alt,
  className,
  expandable = false,
  variant = 'full',
  tone = 'light',
}: {
  src: string;
  alt: string;
  className?: string;
  expandable?: boolean;
  variant?: 'full' | 'thumbnail' | 'auto';
  tone?: 'light' | 'dark';
}) {
  const [open, setOpen] = useState(false);
  const autoQuery = useMediaContentType(src);

  const content = match(variant)
    .with('thumbnail', () => (
      <MediaThumbnail src={src} alt={alt} tone={tone} className={className} />
    ))
    .with('full', () => <MediaFull src={src} alt={alt} className={className} />)
    .with('auto', () => {
      if (autoQuery.isPending) {
        return <MediaFallback type="loading" alt={alt} className={className} />;
      }
      if (autoQuery.isError) {
        return <MediaFallback type="error" alt={alt} className={className} />;
      }
      return autoQuery.data.type === 'image' ? (
        <MediaFull src={src} alt={alt} className={className} />
      ) : (
        <MediaThumbnail src={src} alt={alt} tone={tone} className={className} />
      );
    })
    .exhaustive();

  if (!expandable) return content;

  // the button wraps the media rather than overlaying it, so nothing here is positioned and it
  // scrolls under sticky headers like any other cell content
  return (
    <>
      <button
        type="button"
        // rounding should be kept in sync with the content itself
        className="block cursor-zoom-in rounded-md focus:ring-2 focus:ring-corso-blue-600 focus:outline-none"
        onClick={() => setOpen(true)}
      >
        {content}
        <span className="sr-only">View full size</span>
      </button>
      <MediaLightbox
        title={alt}
        items={[{ src, alt }]}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
